import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { seoHead } from "@/lib/seo";
import {
  PRODUCTS,
  CATEGORIES,
  isSoldOut,
  productStock,
  stockLabel,
  SHIPPING_NOTE,
  shopItemListJsonLd,
  shopBreadcrumbsJsonLd,
} from "@/lib/shop-products";
import { reportShopRouteError, useShopSentry } from "@/lib/shop-telemetry";
import { useCart, formatUsd } from "@/lib/cart";

export const Route = createFileRoute("/shop")({
  head: () =>
    seoHead({
      path: "/shop",
      title:
        "Coffman Cove Lodging Shop — Alaska Lodge Apparel, Gear & Keepsakes",
      description:
        "Shop Coffman Cove Lodging tees, hoodies, hats, aprons, engraved fillet knives, camp mugs, and Alaska challenge coins. Ships direct to your door in the lower 48.",
      extraMeta: [
        {
          name: "keywords",
          content:
            "Coffman Cove Lodging shop, Alaska fishing lodge apparel, lodge tee, lodge hoodie, leather patch hat, engraved fillet knife, enamel camp mug, Alaska challenge coin, Prince of Wales Island gear",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(shopItemListJsonLd()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(shopBreadcrumbsJsonLd()),
        },
      ],
    }),
  component: Shop,
  errorComponent: ShopError,
  notFoundComponent: ShopNotFound,
});

function ShopError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    reportShopRouteError(error, { view: "index" });
  }, [error]);
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 text-center">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
        Lodge Shop
      </p>
      <h1 className="mt-3 font-display text-3xl sm:text-4xl">
        The shop hit a snag loading.
      </h1>
      <p className="mt-3 text-muted-foreground">
        This is usually a momentary hiccup — refresh or retry and it should come right back. If it keeps happening, give us a call and we'll take your order over the phone.
      </p>
      {import.meta.env.DEV && error?.message && (
        <pre className="mt-6 mx-auto max-w-xl overflow-auto rounded-md border border-border bg-card p-3 text-left text-xs text-muted-foreground">
          {error.message}
        </pre>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="rounded-md bg-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
        >
          Retry
        </button>
        <Link
          to="/contact"
          className="rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
        >
          Contact the lodge
        </Link>
      </div>
    </div>
  );
}

function ShopNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 text-center">
      <h1 className="font-display text-3xl">Shop page not found</h1>
      <p className="mt-3 text-muted-foreground">
        That link may have moved. Head back to the main shop to browse everything currently on the shelves.
      </p>
      <Link
        to="/shop"
        className="mt-6 inline-block rounded-md bg-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)]"
      >
        Back to the shop
      </Link>
    </div>
  );
}


function Shop() {
  useShopSentry("index");
  const [cat, setCat] = useState<(typeof CATEGORIES)[number]>("All");
  const { add, count: cartCount, subtotal: cartTotal } = useCart();

  const filtered = cat === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat);

  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">Lodge Shop</p>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-3">
            <h1 className="font-display text-4xl sm:text-5xl text-balance max-w-2xl text-white">
              Take a piece of the cove home.
            </h1>
            <Link
              to="/cart"
              className="rounded-lg border border-white/20 bg-white/10 px-5 py-3 text-sm text-white transition-colors hover:border-[var(--color-ember)]"
            >
              <span className="text-white/70">Cart</span>{" "}
              <span className="font-semibold">
                {cartCount} item{cartCount === 1 ? "" : "s"}
              </span>
              <span className="mx-2 text-white/50">·</span>
              <span className="font-semibold text-white">{formatUsd(cartTotal)}</span>
            </Link>
          </div>
          <p className="mt-5 max-w-2xl text-sm text-white/85">
            Ships directly to your home in the lower 48 states. Lodge guests can pick up on arrival — no shipping charged.
          </p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="mt-2 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-sm uppercase tracking-wider transition-colors ${
                cat === c
                  ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                  : "border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <article
              key={p.id}
              className="group relative rounded-xl border border-border bg-card overflow-hidden hover:border-[var(--color-ember)] transition-colors flex flex-col"
            >
              <Link
                to="/shop/$productId"
                params={{ productId: p.id }}
                className="block relative aspect-[4/3] bg-gradient-to-br from-[var(--color-navy)] to-[var(--color-navy-deep)] flex items-center justify-center overflow-hidden"
                aria-label={`View ${p.name}`}
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <span className="font-display text-4xl sm:text-5xl md:text-6xl text-[var(--color-ember)] opacity-30">
                    {p.name.charAt(0)}
                  </span>
                )}
                {p.badge && (
                  <span className="absolute top-3 left-3 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[var(--color-ember)] text-[var(--color-ember-foreground)]">
                    {p.badge}
                  </span>
                )}
              </Link>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-ember)]">{p.category}</p>
                <Link
                  to="/shop/$productId"
                  params={{ productId: p.id }}
                  className="mt-1 font-display text-xl hover:text-[var(--color-ember)] transition-colors"
                >
                  {p.name}
                </Link>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.blurb}</p>
                <p
                  className={`mt-3 text-[11px] uppercase tracking-[0.18em] ${
                    isSoldOut(p)
                      ? "text-muted-foreground"
                      : "text-[var(--color-ember)]"
                  }`}
                >
                  {stockLabel(isSoldOut(p) ? "sold_out" : productStock(p))}
                </p>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="font-semibold text-lg">${p.price}</span>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/shop/$productId"
                      params={{ productId: p.id }}
                      className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
                    >
                      Details
                    </Link>
                    {isSoldOut(p) ? (
                      <span className="rounded-md border border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Sold out
                      </span>
                    ) : p.variants?.length ? (
                      <Link
                        to="/shop/$productId"
                        params={{ productId: p.id }}
                        className="rounded-md bg-[var(--color-ember)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
                      >
                        Choose options
                      </Link>
                    ) : (
                      <button
                        onClick={() => add(p.id)}
                        className="rounded-md bg-[var(--color-ember)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-12 text-xs text-muted-foreground text-center max-w-2xl mx-auto">
          {SHIPPING_NOTE}
        </p>
      </div>
    </div>
  );
}
